import React from 'react'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Tooltip, Typography } from '@mui/material'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import format from 'date-fns/format'
import MorePassengerDialog from 'pages/Customers/vendor/Faber/v2-customer-components/MorePassengersDialog'
import { SmartDelayFormRows } from 'pages/Customers/vendor/Faber/v2-customer-components/SmartDelayFormLogsV2'
import { SMSAnomalyLogType } from 'types/api/faber/anomalies.type'
import { BillWaiverAuditLogType } from 'types/api/faber/customerAuditLog.type'
import { DataCDROverviewType } from 'types/api/faber/dataUsage.type'
import { ICustomerSubHistoryDetails } from 'types/api/faber/subscription.type'

export const ESIM_REPLACMENT = 'esimReplacement'
export const STATUS_UPDATE = 'statusUpdate'
export const DATA_USAGE = 'dataUsage'
export const CUSTOMER_DATA_USAGE = 'customerDataUsage'
export const CUSTOMER_USAGE_BREAKDOWN = 'customerUsageBreakdown'
export const PAYMENT_LOG = 'paymentLog'
export const TRANSACTION_DETAIL = 'transactionDetail'
export const WINBACK_LOG = 'winbackLog'
export const EMAIL_A_LOG = 'emailAnomalyLog'
export const SMS_A_LOG = 'smsAnomalyLog'
export const ALL_EMAILS_LOG = 'allEmailsLog'
export const SMART_DELAY_LOG = 'smartDelayLog'
export const SUB_HISTORY = 'subHistory'

type LogType =
  | 'esimReplacement'
  | 'statusUpdate'
  | 'dataUsage'
  | 'customerUsageBreakdown'
  | 'paymentLog'
  | 'transactionDetail'
  | 'winbackLog'
  | 'emailAnomalyLog'
  | 'smsAnomalyLog'
  | 'allEmailsLog'
  | 'smartDelayLog'
  | 'subHistory'
  | 'customerDataUsage'
const waiverInfoText = `Current bill waiver in place. \n No new waivers allowed until current waiver has expired.`
export function generateColumnHeader(logType: LogType) {
  switch (logType) {
    case ESIM_REPLACMENT: {
      return [
        {
          field: 'updatedAt',
          headerName: 'Updated at',
          width: 150,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
        },
        { field: 'userId', headerName: 'Customer ID', width: 150 },
        { field: 'oldICCID', headerName: 'Old ICCID', width: 150 },
        { field: 'newICCID', headerName: 'New ICCID', width: 150 },
        { field: 'updatedBy', headerName: 'Updated By', width: 150 },
        {
          field: 'updateStatus',
          headerName: 'Update Status',
          width: 150
        },
        {
          field: 'remarks',
          headerName: 'Remarks',
          width: 150
        }
      ]
    }
    case STATUS_UPDATE: {
      return [
        {
          field: 'updatedAt',
          headerName: 'Date Changed',
          valueFormatter: ({ value }) =>
            format(new Date(value), 'dd MMM yyyy HH:mm'),
          width: 100
        },
        { field: 'nextPlan', headerName: 'Next Plan', width: 100 },
        { field: 'accStatus', headerName: 'Account Status', width: 100 },
        { field: 'updatedBy', headerName: 'Updated By', width: 100 },
        {
          field: 'remarks',
          headerName: 'Remarks',
          width: 100
        }
      ]
    }
    case DATA_USAGE: {
      return [
        {
          field: 'Timestamp',
          headerName: 'Timestamp',
          sortable: true,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
        },
        {
          field: 'Type',
          headerName: 'Type',
          sortable: false,
          width: 150
        },
        {
          field: 'ExpiryDate',
          headerName: 'Expiry Date',
          sortable: false,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : '',
          renderCell: (params: { row: DataCDROverviewType }) => {
            if (
              params.row.Type === 'Lite Plan' ||
              params.row.Type === 'Pro Plan' ||
              params.row.Type === 'Customer Usage'
            ) {
              return ''
            }
          }
        },
        {
          field: 'DataAllocatedMB',
          headerName: 'Data Allocated (MB)',
          sortable: false,
          width: 50,
          renderCell: (params: { row: DataCDROverviewType }) => {
            if (params.row.Type === 'Customer Usage') {
              return ''
            }
          }
        }
      ]
    }
    case CUSTOMER_DATA_USAGE: {
      return [
        {
          field: 'Timestamp',
          headerName: 'Timestamp',
          sortable: true,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
        },
        {
          field: 'DataUsedMB',
          headerName: 'Data Used (MB)',
          sortable: false,
          width: 50
        },
        { field: 'Country', headerName: 'Country', sortable: false },
        { field: 'Network', headerName: 'Network', sortable: false }
      ]
    }
    case CUSTOMER_USAGE_BREAKDOWN: {
      return [
        {
          field: 'Timestamp',
          headerName: 'Timestamp',
          sortable: false,
          minWidth: 100,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
        },
        { field: 'CDRID', headerName: 'CDRID', sortable: false, minWidth: 300 },
        { field: 'IMSI', headerName: 'IMSI', sortable: false, minWidth: 50 },
        {
          field: 'DataUsedMB',
          headerName: 'Data Used MB',
          sortable: false,
          minWidth: 100
        },
        {
          field: 'Country',
          headerName: 'Country',
          sortable: false,
          minWidth: 100
        },
        {
          field: 'Network',
          headerName: 'Network',
          sortable: false,
          minWidth: 100
        }
      ]
    }
    case PAYMENT_LOG: {
      return [
        {
          field: 'amount',
          headerName: 'Amount',
          flex: 0.5,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'trn',
          headerName: 'Transaction Reference Number',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'paymentType',
          headerName: 'Payment Type',
          flex: 1,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'updated',
          headerName: 'Updated Date',
          flex: 1,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'updatedBy',
          headerName: 'Updated By',
          flex: 1,
          minWidth: 100,
          filterable: false
        }
      ]
    }
    case TRANSACTION_DETAIL: {
      return [
        {
          field: 'amount',
          headerName: 'Amount',
          flex: 0.5,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'trn',
          headerName: 'Transaction Reference Number',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'paymentType',
          headerName: 'Payment Type',
          flex: 1,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'updated',
          headerName: 'Updated Date',
          flex: 1,
          minWidth: 100,
          filterable: false,
          valueFormatter: ({ value }) =>
            value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
        },
        {
          field: 'updatedBy',
          headerName: 'Updated By',
          flex: 1,
          minWidth: 100,
          filterable: false
        }
      ]
    }
    case WINBACK_LOG: {
      return [
        {
          field: 'updatedAt',
          headerName: 'Updated At',
          flex: 1.5,
          minWidth: 100,
          filterable: false,
          valueFormatter: ({ value }) =>
            format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
          field: 'updatedBy',
          headerName: 'Updated By',
          flex: 1.5,
          minWidth: 200,
          filterable: false
        },
        {
          field: 'type',
          headerName: 'Type Of Winback',
          flex: 1.5,
          minWidth: 100,
          filterable: true,
          renderCell: (params: { row: BillWaiverAuditLogType }) => {
            if (params.row.updateStatus.toLowerCase().includes('voucher')) {
              return 'VOUCHER'
            } else {
              return 'WAIVER'
            }
          }
        },
        {
          field: 'status',
          headerName: 'Waiver Status',
          flex: 1.5,
          minWidth: 100,
          filterable: true,
          renderCell: (params: { row: BillWaiverAuditLogType }) => {
            if (params.row.updateStatus.toLowerCase().includes('waiver')) {
              return (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: params.row.status === 'EXPIRED' ? 'red' : 'green'
                    }}
                  >
                    {params.row.status}
                  </Typography>
                  {params.row.status === 'ACTIVE' ? (
                    <Tooltip
                      title={waiverInfoText}
                      followCursor={true}
                      PopperProps={{
                        sx: { maxWidth: '165px', textAlign: 'center' }
                      }}
                    >
                      <InfoIcon color={'secondary'} />
                    </Tooltip>
                  ) : (
                    ''
                  )}
                </>
              )
            }
          }
        },
        {
          field: 'waiverID',
          headerName: 'Waiver ID',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'months',
          headerName: 'Waiver Months',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'value',
          headerName: 'Waiver Value',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'voucherCreditChange',
          headerName: 'Voucher Credit Change',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'FinalVoucherValue',
          headerName: 'Voucher Final Value',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        }
      ]
    }
    case SMS_A_LOG: {
      return [
        {
          field: 'date',
          headerName: 'Sent on',
          flex: 1,
          minWidth: 30,
          filterable: false,
          valueFormatter: ({ value }) =>
            format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
          field: 'message',
          headerName: 'SMS Message',
          flex: 1.5,
          minWidth: 200,
          filterable: false,
          renderCell: (params: { row: SMSAnomalyLogType }) => (
            <Tooltip title={params.row.message} placement='top-start'>
              <Box>{params.row.message}</Box>
            </Tooltip>
          )
        },
        {
          field: 'SMS_STATUS',
          headerName: 'SMS Status',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'SMS_ERROR_MSG',
          headerName: 'SMS Error Msg',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        }
      ]
    }
    case EMAIL_A_LOG: {
      return [
        {
          field: 'date',
          headerName: 'Sent on',
          flex: 1,
          minWidth: 30,
          filterable: false,
          valueFormatter: ({ value }) =>
            format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
          field: 'EMAIL_TEMPLATE_NAME',
          headerName: 'Email Template Name',
          flex: 1.5,
          minWidth: 150,
          filterable: false
        },
        {
          field: 'EMAIL_STATUS',
          headerName: 'Email Status',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'EMAIL_ERROR_MSG',
          headerName: 'Email Error Msg',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        }
      ]
    }
    case ALL_EMAILS_LOG: {
      return [
        {
          field: 'sender',
          headerName: 'Email',
          flex: 1.5,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'subject',
          headerName: 'Subject',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'campaign_id',
          headerName: 'Template',
          flex: 1,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'send_timestamp',
          headerName: 'Send At',
          flex: 1,
          minWidth: 100,
          filterable: false,
          valueFormatter: ({ value }) =>
            format(new Date(value * 1000), 'dd MMM yyyy HH:mm')
        },
        {
          field: 'delivery_timestamp',
          headerName: 'Deliver At',
          flex: 1,
          minWidth: 100,
          filterable: false,
          valueFormatter: ({ value }) =>
            format(new Date(value * 1000), 'dd MMM yyyy HH:mm')
        }
      ]
    }
    case SMART_DELAY_LOG: {
      return [
        {
          field: 'createdAt',
          headerName: 'Submitted on',
          flex: 1.5,
          minWidth: 30,
          filterable: true,
          valueFormatter: ({ value }) =>
            format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
          field: 'smartDelayId',
          headerName: 'Flight ID',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'mainPassengerEmail',
          headerName: 'Main Passenger Email',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'departureDate',
          headerName: 'Departure Date (UTC)',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'flightCode',
          headerName: 'Flight Code',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'departureAirportCode',
          headerName: 'Departure Airport Code ',
          flex: 1,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'departureAirportNameCountry',
          headerName: 'Departure Airport ',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'arrivalAirportCode',
          headerName: 'Arrival Airport Code ',
          flex: 1,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'arrivalAirportNameCountry',
          headerName: 'Arrival Airport ',
          flex: 1.5,
          minWidth: 100,
          filterable: false
        },
        {
          field: 'moreInfo',
          headerName: 'More Passengers',
          flex: 1.5,
          minWidth: 100,
          filterable: false,

          renderCell: (params: { row: SmartDelayFormRows }) => (
            <MorePassengerDialog
              morePassengersDetails={params.row.morePassengersDetails}
            />
          )
        }
      ]
    }
    case SUB_HISTORY: {
      return [
        {
          field: 'SubscriptionStartAt',
          headerName: 'Subscription started on',
          flex: 1.5,
          minWidth: 30,
          filterable: false,
          renderCell: (params: { row: ICustomerSubHistoryDetails }) => {
            const unixTimestampSubStart =
              params.row.dates.subscriptionStartAt.unixSeconds
            const sgTime = new Date(
              unixTimestampSubStart * 1000
            ).toLocaleString('en-US', { timeZone: 'Asia/Singapore' })

            const formattedSgTime = format(
              new Date(sgTime),
              'dd MMM yyyy HH:mm'
            )
            return (
              <AbstractDataGridTooltip>
                {formattedSgTime}
              </AbstractDataGridTooltip>
            )
          }
        },
        {
          field: 'status',
          headerName: 'Status',
          flex: 1.3,
          minWidth: 10,
          filterable: false
        },
        {
          field: 'planName',
          headerName: 'Plan Type',
          flex: 1,
          minWidth: 30,
          filterable: false
        },
        {
          field: 'subscriptionEnded',
          headerName: 'Subscription cancelled on',
          flex: 1.5,
          minWidth: 30,
          filterable: false,
          renderCell: (params: { row: ICustomerSubHistoryDetails }) => {
            return (
              <AbstractDataGridTooltip>
                {params.row.status === 'CANCELLED'
                  ? format(new Date(params.row.UpdatedAt), 'dd MMM yyyy HH:mm')
                  : '-'}
              </AbstractDataGridTooltip>
            )
          }
        },

        {
          field: 'cycle_count',
          headerName: 'Cycle Count',
          flex: 1,
          minWidth: 20,
          filterable: false
        },

        {
          field: 'lastBillingDate',
          headerName: 'Last Billing Date',
          flex: 1.5,
          minWidth: 30,
          filterable: false,
          renderCell: (params: { row: ICustomerSubHistoryDetails }) => {
            const unixTimestamp = params.row.dates.lastBillingDate.unixSeconds
            const sgTime = new Date(unixTimestamp * 1000).toLocaleString(
              'en-US',
              { timeZone: 'Asia/Singapore' }
            )

            const formattedSgTime = format(
              new Date(sgTime),
              'dd MMM yyyy HH:mm'
            )
            return (
              <AbstractDataGridTooltip>
                {formattedSgTime}
              </AbstractDataGridTooltip>
            )
          }
        },
        {
          field: 'nextBillingDate',
          headerName: 'Next Billing Date',
          flex: 1.5,
          minWidth: 30,
          filterable: false,
          renderCell: (params: { row: ICustomerSubHistoryDetails }) => {
            const unixTimestamp = params.row.dates.nextBillingDate.unixSeconds
            const sgTime = new Date(unixTimestamp * 1000).toLocaleString(
              'en-US',
              { timeZone: 'Asia/Singapore' }
            )
            const formattedSgTime = format(
              new Date(sgTime),
              'dd MMM yyyy HH:mm'
            )

            return params.row.status === 'ACTIVE' ? (
              <AbstractDataGridTooltip>
                {formattedSgTime}
              </AbstractDataGridTooltip>
            ) : (
              ''
            )
          }
        },

        {
          field: 'price',
          headerName: 'Price',
          flex: 1,
          minWidth: 30,
          filterable: false,
          renderCell: (params: { row: ICustomerSubHistoryDetails }) => {
            return `${params.row.currency}${params.row.price}`
          }
        },
        {
          field: 'paymentType',
          headerName: 'Payment Type',
          flex: 1.5,
          minWidth: 30,
          filterable: false
        }
      ]
    }

    default: {
      return []
    }
  }
}
